/**
 * Standalone Keycloak-gated page for Arunesh's onboarding_intake pipeline:
 * upload a .docx, see it prefilled into the actual canonical intake form
 * (grouped sections, one editable control per field, driven by
 * canonical_schema.json -- the same contract the plan's README describes
 * the frontend building the form from), fix doubtful answers, submit.
 *
 * Not part of the TFC wizard (TfcDashOnboardingPage.jsx) and doesn't touch
 * it -- that flow already works and is deployed. This talks only to the new
 * onboarding_intake backend; the response shape it consumes (separate
 * `values`/`review` maps, joined against the schema here) is exactly what
 * prefill()/check() already return -- no backend change.
 *
 * Local testing: point REACT_APP_INTAKE_API_BASE at wherever
 * onboarding_intake is actually running, e.g.
 *   REACT_APP_INTAKE_API_BASE=http://localhost:5055 npm start
 * against onboarding_services/run_intake_dev.py.
 */
import React, { useEffect, useRef, useState } from "react";
import { Button, Card } from "../design/primitives";
import { color, radius, space, text, weight } from "../design/tokens";
import { getKeycloakInstance, initKeycloak, setCurrentRealm } from "../keycloak";

// Pinned for the same reason TfcDashOnboardingPage.jsx pins it: without this,
// getCurrentRealm()'s ambient resolution (URL -> sessionStorage -> the
// app-wide "Idenza" default) can authenticate into whatever realm is already
// cached in the browser, which the backend's adapter registry then rejects
// with "This account can't use onboarding." even though login itself succeeded.
const TEST_REALM = "tfcdas";

const STAGE_INTAKE_API_BASE = "/intake-api";
const INTAKE_API_BASE = (process.env.REACT_APP_INTAKE_API_BASE || STAGE_INTAKE_API_BASE).replace(/\/+$/, "");
const NA = "N/A";

async function authHeader() {
  const keycloak = getKeycloakInstance();
  try {
    await keycloak.updateToken(30);
  } catch {
    // session ended; send what we have, a 401 tells the tester to sign in again
  }
  return keycloak.token ? { Authorization: `Bearer ${keycloak.token}` } : {};
}

async function asJsonOrThrow(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

async function intakeSchema() {
  // No auth: no customer data in the schema itself (flask_bp.py leaves this route open).
  return asJsonOrThrow(await fetch(`${INTAKE_API_BASE}/onboarding/intake/schema`));
}

async function intakePrefill(file) {
  const form = new FormData();
  form.append("file", file);
  const headers = await authHeader();
  const res = await fetch(`${INTAKE_API_BASE}/onboarding/intake/prefill`, { method: "POST", headers, body: form });
  return asJsonOrThrow(res);
}

async function intakeCheck(values) {
  const headers = { ...(await authHeader()), "Content-Type": "application/json" };
  const res = await fetch(`${INTAKE_API_BASE}/onboarding/intake/check`, {
    method: "POST", headers, body: JSON.stringify({ values }),
  });
  return asJsonOrThrow(res);
}

/* ========================================================================
 * Keycloak auth gate -- same pattern as TfcDashOnboardingPage.jsx's own
 * AuthGate, duplicated rather than imported: this is a standalone page and
 * shouldn't create a dependency on the wizard file.
 * ========================================================================
 */
function hasAuthParamsInUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.has("code") || params.has("session_state");
  } catch {
    return false;
  }
}

function AuthGate({ children }) {
  const [state, setState] = useState("checking"); // checking | authenticated | redirecting | error
  const [errorMessage, setErrorMessage] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    let cancelled = false;

    (async () => {
      try {
        setCurrentRealm(TEST_REALM);
        const keycloak = await initKeycloak();
        if (cancelled) return;
        if (keycloak?.authenticated && keycloak.token) {
          setState("authenticated");
          return;
        }
        if (hasAuthParamsInUrl()) {
          setState("checking");
          return;
        }
        setState("redirecting");
        await keycloak.login({ redirectUri: window.location.href });
      } catch (err) {
        if (cancelled) return;
        setErrorMessage(err?.message || "Could not reach the login service.");
        setState("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (state === "authenticated") return children;

  if (state === "error") {
    return (
      <div style={{ maxWidth: 480, margin: "80px auto", padding: space[5], textAlign: "center" }}>
        <div style={{ fontSize: text.lg, fontWeight: weight.semibold, color: color.text.primary, marginBottom: space[2] }}>
          Couldn't sign you in
        </div>
        <div style={{ fontSize: text.base, color: color.text.tertiary, marginBottom: space[4] }}>{errorMessage}</div>
        <button type="button" onClick={() => window.location.reload()} style={{ cursor: "pointer" }}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 480, margin: "80px auto", padding: space[5], textAlign: "center", color: color.text.tertiary }}>
      {state === "redirecting" ? "Redirecting to sign in…" : "Checking your session…"}
    </div>
  );
}

/* ========================================================================
 * Field inputs -- one per canonical type. Simple types get a real control;
 * structured types (address, person_list, money, activity, ...) get a JSON
 * editor rather than a bespoke widget each, to keep every field genuinely
 * editable without a widget-per-type effort this isn't scoped for.
 * ========================================================================
 */
const inputStyle = { width: "100%", padding: space[1], border: `1px solid ${color.border.subtle}`, borderRadius: radius.sm, fontSize: text.sm };

function JsonFieldInput({ value, onChange }) {
  const [rawText, setRawText] = useState(() => JSON.stringify(value, null, 2));
  const [err, setErr] = useState(null);
  useEffect(() => {
    setRawText(JSON.stringify(value, null, 2));
  }, [value]);
  return (
    <div>
      <textarea
        value={rawText}
        rows={3}
        onChange={(e) => setRawText(e.target.value)}
        onBlur={() => {
          try {
            onChange(JSON.parse(rawText));
            setErr(null);
          } catch {
            setErr("invalid JSON — not saved");
          }
        }}
        style={{ ...inputStyle, fontFamily: "monospace", fontSize: "0.8em" }}
      />
      {err && <div style={{ color: color.danger.text, fontSize: text.sm }}>{err}</div>}
    </div>
  );
}

function FieldInput({ def, value, onChange }) {
  const type = def["x-type"];
  const options = def["x-options"];
  const isNA = value === NA;

  if (type === "text" || type === "date") {
    return (
      <input
        type={type === "date" && !isNA ? "date" : "text"}
        value={isNA ? "" : value || ""}
        placeholder={isNA ? NA : ""}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
      />
    );
  }

  if (type === "checkbox_single" && options) {
    const selected = !isNA && value && typeof value === "object" ? value.selected : "";
    return (
      <select value={selected || ""} onChange={(e) => onChange({ ...(typeof value === "object" ? value : {}), selected: e.target.value })} style={inputStyle}>
        <option value="">{isNA ? NA : "—"}</option>
        {Object.entries(options).map(([code, label]) => (
          <option key={code} value={code}>
            {label}
          </option>
        ))}
      </select>
    );
  }

  if (type === "checkbox_multi" && options) {
    const selected = !isNA && value && typeof value === "object" && Array.isArray(value.selected) ? value.selected : [];
    const toggle = (code) => {
      const next = selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code];
      onChange({ ...(typeof value === "object" ? value : {}), selected: next });
    };
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: space[2] }}>
        {Object.entries(options).map(([code, label]) => (
          <label key={code} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: text.sm }}>
            <input type="checkbox" checked={selected.includes(code)} onChange={() => toggle(code)} />
            {label}
          </label>
        ))}
      </div>
    );
  }

  if (type === "yes_no" || type === "yes_no_explain") {
    const answer = !isNA && value && typeof value === "object" ? value.answer : undefined;
    const explanation = !isNA && value && typeof value === "object" ? value.explanation : "";
    return (
      <div style={{ display: "flex", gap: space[2] }}>
        <select
          value={answer === true ? "yes" : answer === false ? "no" : ""}
          onChange={(e) => onChange({ ...(typeof value === "object" ? value : {}), answer: e.target.value === "yes" })}
          style={{ ...inputStyle, width: "auto" }}
        >
          <option value="">{isNA ? NA : "—"}</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
        {type === "yes_no_explain" && (
          <input
            type="text"
            placeholder="explanation"
            value={explanation || ""}
            onChange={(e) => onChange({ ...(typeof value === "object" ? value : {}), explanation: e.target.value })}
            style={{ ...inputStyle, flex: 1 }}
          />
        )}
      </div>
    );
  }

  // address, contact, tin, formation, naics, dated_text, country_list, person_list,
  // money, money_list, money_with_text, activity, activity_countries, balance,
  // url_list, number, yes_no_countries, yes_no_percent: edit the raw JSON.
  return <JsonFieldInput value={value} onChange={onChange} />;
}

/* ========================================================================
 * The intake form
 * ========================================================================
 */
// JSON object key order isn't something to depend on (Flask's jsonify sorts
// keys alphabetically by default, so the schema's own "x-groups" arrives
// alphabetical over the wire: access, account, activity, ... — not the
// question order a reviewer expects). Pinned here to match the order
// canonical/fields.yaml declares its groups in.
const GROUP_ORDER = [
  "account", "individual", "entity", "ownership", "business", "activity",
  "funds", "geo", "sanctions", "ffi", "program", "cash", "access",
];

function groupedFields(schema, review) {
  if (!schema) return [];
  const groups = schema["x-groups"] || {};
  const byGroup = {};
  for (const [key, def] of Object.entries(schema.properties || {})) {
    const g = def["x-group"] || "other";
    (byGroup[g] = byGroup[g] || []).push({ key, def, review: review?.[key] });
  }
  const order = [...GROUP_ORDER, ...Object.keys(groups).filter((g) => !GROUP_ORDER.includes(g))];
  return order
    .filter((g) => groups[g])
    .map((g) => ({ key: g, label: groups[g], fields: byGroup[g] || [] }))
    .filter((g) => g.fields.length > 0);
}

function IntakeTestPage() {
  const [schema, setSchema] = useState(null);
  const [schemaError, setSchemaError] = useState(null);
  const [file, setFile] = useState(null);
  const [prefillState, setPrefillState] = useState({ running: false, error: null, result: null });
  const [formValues, setFormValues] = useState(null);
  const [checkState, setCheckState] = useState({ running: false, error: null, result: null });

  useEffect(() => {
    intakeSchema()
      .then(setSchema)
      .catch((e) => setSchemaError(e.message));
  }, []);

  async function runPrefill() {
    if (!file) return;
    setPrefillState({ running: true, error: null, result: null });
    setCheckState({ running: false, error: null, result: null });
    setFormValues(null);
    try {
      const result = await intakePrefill(file);
      setPrefillState({ running: false, error: null, result });
      setFormValues(result.values || {});
    } catch (e) {
      setPrefillState({ running: false, error: e.message, result: null });
    }
  }

  function setFieldValue(key, value) {
    setFormValues((v) => ({ ...(v || {}), [key]: value }));
  }

  async function runSubmit() {
    if (!formValues) return;
    setCheckState({ running: true, error: null, result: null });
    try {
      const result = await intakeCheck(formValues);
      setCheckState({ running: false, error: null, result });
    } catch (e) {
      setCheckState({ running: false, error: e.message, result: null });
    }
  }

  const issuesByKey = {};
  for (const i of checkState.result?.issues || []) {
    if (i.key) (issuesByKey[i.key] = issuesByKey[i.key] || []).push(i);
  }

  const sections = groupedFields(schema, prefillState.result?.review);

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: `${space[6]}px ${space[5]}px` }}>
      <h1 style={{ margin: 0, fontSize: text["2xl"], fontWeight: weight.semibold, color: color.text.primary }}>
        Onboarding intake form
      </h1>
      <div style={{ marginTop: space[1], marginBottom: space[5], fontSize: text.sm, color: color.text.tertiary }}>
        Word questionnaire → prefill() → this form → fix doubtful answers → Submit → check_submission(). Separate from
        the TFC wizard. Target: <code>{INTAKE_API_BASE}</code>
      </div>

      {schemaError && (
        <div style={{ padding: space[3], background: color.danger.bg, color: color.danger.text, borderRadius: radius.md, marginBottom: space[4] }}>
          Couldn't load the form schema: {schemaError}
        </div>
      )}

      <Card header={{ title: "Upload the completed questionnaire" }}>
        <input type="file" accept=".docx" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        <div style={{ marginTop: space[3] }}>
          <Button variant="primary" size="sm" disabled={!file || !schema || prefillState.running} onClick={runPrefill}>
            {prefillState.running ? "Reading…" : "Read and pre-fill"}
          </Button>
        </div>
        {prefillState.error && (
          <div style={{ marginTop: space[3], padding: space[3], background: color.danger.bg, color: color.danger.text, borderRadius: radius.md }}>
            {prefillState.error}
          </div>
        )}
        {prefillState.result?.warnings?.length > 0 && (
          <ul style={{ marginTop: space[3], fontSize: text.sm, color: color.warning.text }}>
            {prefillState.result.warnings.map((w, idx) => (
              <li key={idx}>{w.message}</li>
            ))}
          </ul>
        )}
      </Card>

      {formValues && sections.length > 0 && (
        <>
          {sections.map((section) => (
            <Card key={section.key} header={{ title: section.label }} style={{ marginTop: space[4] }}>
              <div style={{ display: "flex", flexDirection: "column", gap: space[4] }}>
                {section.fields.map(({ key, def, review }) => {
                  const fieldIssues = issuesByKey[key];
                  const tone = fieldIssues ? "danger" : review?.needs_review ? "warning" : null;
                  return (
                    <div
                      key={key}
                      id={`field-${key}`}
                      style={{
                        padding: space[2],
                        borderRadius: radius.sm,
                        background: tone === "danger" ? color.danger.bg : tone === "warning" ? color.warning.bg : "transparent",
                      }}
                    >
                      <label style={{ display: "block", fontSize: text.sm, fontWeight: weight.semibold, color: color.text.primary, marginBottom: space[1] }}>
                        {def.title}
                        {def["x-required"] && <span style={{ color: color.danger.text }}> *</span>}
                        {def["x-sensitive"] && (
                          <span style={{ fontWeight: weight.regular, color: color.text.tertiary, fontSize: "0.85em" }}> (sensitive)</span>
                        )}
                      </label>
                      <FieldInput def={def} value={formValues[key]} onChange={(v) => setFieldValue(key, v)} />
                      {review?.confidence < 1 && review?.status === "filled" && (
                        <div style={{ marginTop: space[1], fontSize: text.sm, color: color.text.tertiary }}>
                          confidence {review.confidence} · {(review.notes || []).join("; ")}
                        </div>
                      )}
                      {fieldIssues?.map((i, idx) => (
                        <div key={idx} style={{ marginTop: space[1], fontSize: text.sm, color: color.danger.text }}>
                          [{i.severity}] {i.message}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}

          <div style={{ marginTop: space[4], display: "flex", justifyContent: "flex-end" }}>
            <Button variant="primary" disabled={checkState.running} onClick={runSubmit}>
              {checkState.running ? "Checking…" : "Submit"}
            </Button>
          </div>
        </>
      )}

      {checkState.error && (
        <div style={{ marginTop: space[4], padding: space[3], background: color.danger.bg, color: color.danger.text, borderRadius: radius.md }}>
          {checkState.error}
        </div>
      )}

      {checkState.result && (
        <Card
          header={{
            title: checkState.result.outcome?.can_submit ? "Ready to submit" : "Fix the items above",
          }}
          style={{ marginTop: space[4] }}
        >
          <div style={{ fontSize: text.sm, color: color.text.primary }}>
            status {checkState.result.outcome?.status} · can_submit {String(checkState.result.outcome?.can_submit)} ·
            subscriber {checkState.result.subscriber?.id} (rules v{checkState.result.subscriber?.rules_version},{" "}
            {checkState.result.subscriber?.rules_approval})
          </div>
          {checkState.result.issues?.length > 0 && (
            <ul style={{ marginTop: space[2], fontSize: text.sm, color: color.text.secondary }}>
              {checkState.result.issues.map((i, idx) => (
                <li key={idx}>
                  {i.key ? (
                    <a
                      href={`#field-${i.key}`}
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById(`field-${i.key}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
                      }}
                    >
                      [{i.severity}] {i.message}
                    </a>
                  ) : (
                    <>
                      [{i.severity}] {i.message}
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {checkState.result && (
        <Card
          header={{
            title: "Screening subjects",
            right: (
              <span style={{ fontSize: text.sm, color: color.text.tertiary }}>
                who this step says to screen — no screening provider is actually called here
              </span>
            ),
          }}
          style={{ marginTop: space[4] }}
        >
          {checkState.result.screening_subjects?.length > 0 ? (
            <table style={{ width: "100%", fontSize: text.sm, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: `1px solid ${color.border.subtle}` }}>
                  <th style={{ padding: space[1] }}>name</th>
                  <th style={{ padding: space[1] }}>type</th>
                  <th style={{ padding: space[1] }}>from fields</th>
                </tr>
              </thead>
              <tbody>
                {checkState.result.screening_subjects.map((s, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${color.border.subtle}` }}>
                    <td style={{ padding: space[1] }}>{s.name}</td>
                    <td style={{ padding: space[1] }}>{s.type}</td>
                    <td style={{ padding: space[1], fontFamily: "monospace", fontSize: "0.85em" }}>{(s.source_fields || []).join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ fontSize: text.sm, color: color.text.tertiary }}>No subjects extracted.</div>
          )}
        </Card>
      )}

      {checkState.result?.tm_profile && (
        <Card header={{ title: "TM baseline" }} style={{ marginTop: space[4] }}>
          <div style={{ fontSize: text.sm, color: color.text.primary, marginBottom: space[2] }}>
            products: {(checkState.result.tm_profile.products || []).join(", ") || "—"} · ruleset hints:{" "}
            {(checkState.result.tm_profile.ruleset_hints || []).map((h) => h.tag).join(", ") || "none"}
          </div>
          {checkState.result.tm_profile.expected_activity?.length > 0 && (
            <table style={{ width: "100%", fontSize: text.sm, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: `1px solid ${color.border.subtle}` }}>
                  <th style={{ padding: space[1] }}>channel</th>
                  <th style={{ padding: space[1] }}>monthly count</th>
                  <th style={{ padding: space[1] }}>monthly value</th>
                </tr>
              </thead>
              <tbody>
                {checkState.result.tm_profile.expected_activity.map((a, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${color.border.subtle}` }}>
                    <td style={{ padding: space[1] }}>{a.channel}</td>
                    <td style={{ padding: space[1] }}>
                      {a.monthly_count_min}–{a.monthly_count_max}
                    </td>
                    <td style={{ padding: space[1] }}>
                      {a.currency} {a.monthly_value_min}–{a.monthly_value_max}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </div>
  );
}

export default function IntakeDraftTestPage() {
  return (
    <AuthGate>
      <IntakeTestPage />
    </AuthGate>
  );
}
